import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SETUP_SAVE_MUTATION_KEY } from '@/core/components/reputation/setup/setup-save-status';
import type {
  Location,
  ReplyPromptConfig,
  ReplyPromptConfigDto,
  UpdateLocationDto,
  Workspace,
} from '@up2stay/up2promo-api-client';
import {
  LocationsApi,
  ReviewsApi,
  IntegrationProvider,
  WorkspacesApi,
} from '@up2stay/up2promo-api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  cn,
  Label,
  previewPanelSurfaceClass,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
} from '@up2stay/up2promo-ui';
import { RefreshCw, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { StarRating } from '@/core/components/reputation/reputation-bits';
import { REPUTATION_SECTION_LABEL_CLASS } from '@/core/components/reputation/reputation-surface';
import { ReviewAuthorAvatar } from '@/core/components/reputation/reviews/review-list-item';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { useDebouncedAutosave } from '@/core/hooks/use-debounced-autosave';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useLocations } from '@/core/hooks/use-locations';
import {
  type ReplyPromptTemplateRow,
  useReplyPromptTemplates,
} from '@/core/hooks/use-reply-prompt-templates';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { resolveReplyPromptTemplateFields } from '@/core/lib/reply-prompt-template-fields';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { reviewsQueryKeys } from '@/core/query-keys/reputation-query-keys';
import { useSetWorkspace } from '@/core/store/workspace.store';

/** Max character lengths (must match backend REPLY_PROMPT_CONFIG_MAX_LENGTHS). */
const REPLY_PROMPT_CONFIG_MAX_LENGTHS = {
  tone: 500,
  considerations: 500,
  mustInclude: 500,
  templateId: 100,
  mergedContext: 2000,
} as const;

const PROVIDER = IntegrationProvider.GOOGLE;

const PREVIEW_SAMPLE_COUNT = 3;

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = copy[i];
    const b = copy[j];
    if (a === undefined || b === undefined) continue;
    copy[i] = b;
    copy[j] = a;
  }
  return copy;
}

/** Pick up to `count` reviews, preferring a mix of different star ratings. */
function pickRandomReviewIds(
  reviews: { id: string; rating: number }[],
  count: number,
): string[] {
  if (reviews.length === 0) return [];
  if (reviews.length <= count) {
    return shuffle(reviews).map((r) => r.id);
  }

  const byRating = new Map<number, { id: string; rating: number }[]>();
  for (const review of reviews) {
    const group = byRating.get(review.rating) ?? [];
    group.push(review);
    byRating.set(review.rating, group);
  }

  const selected: { id: string; rating: number }[] = [];
  const usedIds = new Set<string>();

  const ratingBuckets = shuffle([...byRating.entries()]);

  for (const [, group] of ratingBuckets) {
    if (selected.length >= count) break;
    const pick = shuffle(group)[0];
    if (!pick) continue;
    selected.push(pick);
    usedIds.add(pick.id);
  }

  if (selected.length < count) {
    for (const review of shuffle(reviews.filter((r) => !usedIds.has(r.id)))) {
      if (selected.length >= count) break;
      selected.push(review);
      usedIds.add(review.id);
    }
  }

  return selected.map((r) => r.id);
}

/** Sentinel for "own template" (no preset) – single option instead of "—" and "Custom". */
const CUSTOM_TEMPLATE_VALUE = '__custom__';

/** Template ids that are presets (not own template). */
function isPresetTemplateId(id: string): boolean {
  return id !== '' && id !== CUSTOM_TEMPLATE_VALUE;
}

export type ReplyStyleEditorDict = {
  title?: string;
  description?: string;
  templateLabel?: string;
  templatePlaceholder?: string;
  toneLabel?: string;
  considerationsLabel?: string;
  mustIncludeLabel?: string;
  saveSuccess?: string;
  saveError?: string;
  previewTitle?: string;
  previewDescription?: string;
  previewGenerate?: string;
  previewGenerating?: string;
  previewError?: string;
  noReviewsForPreview?: string;
  previewLocationLabel?: string;
  previewLocationPlaceholder?: string;
  templates?: Record<string, { name?: string; description?: string }>;
  customLabel?: string;
  presetLockedHint?: string;
  detachTitle?: string;
  detachDescription?: string;
  detachConfirm?: string;
  detachCancel?: string;
  previewLive?: string;
  otherReview?: string;
  exampleOf?: string;
  replyFrom?: string;
  previewGenerating2?: string;
};

/** Map template id (kebab-case) to dictionary key (camelCase). */
function templateIdToKey(id: string): string {
  return id.replace(/-([a-z])/g, (_, c) => (c as string).toUpperCase());
}

function payloadSnapshot(payload: ReplyPromptConfig): string {
  return JSON.stringify({
    tone: payload.tone ?? '',
    considerations: payload.considerations ?? '',
    mustInclude: payload.mustInclude ?? '',
    templateId: payload.templateId ?? '',
  });
}

function buildReplyPromptPayload(
  templateId: string,
  tone: string,
  considerations: string,
  mustInclude: string,
  _templates: ReplyPromptTemplateRow[] | undefined,
): ReplyPromptConfig {
  const effectiveTemplateId = isPresetTemplateId(templateId) ? templateId : '';

  // Preset: only persist the template id. Backend resolves live copy from i18n.
  if (effectiveTemplateId) {
    return {
      tone: null,
      considerations: null,
      mustInclude: null,
      templateId: effectiveTemplateId,
      mergedContext: null,
    } as unknown as ReplyPromptConfig;
  }

  const toneTrimmed =
    tone.slice(0, REPLY_PROMPT_CONFIG_MAX_LENGTHS.tone).trim() || null;
  const considerationsTrimmed =
    considerations
      .slice(0, REPLY_PROMPT_CONFIG_MAX_LENGTHS.considerations)
      .trim() || null;
  const mustIncludeTrimmed =
    mustInclude.slice(0, REPLY_PROMPT_CONFIG_MAX_LENGTHS.mustInclude).trim() ||
    null;
  const mergedContext =
    [toneTrimmed, considerationsTrimmed, mustIncludeTrimmed]
      .filter(Boolean)
      .join('\n\n') || undefined;
  return {
    tone: toneTrimmed,
    considerations: considerationsTrimmed,
    mustInclude: mustIncludeTrimmed,
    templateId: null,
    mergedContext: mergedContext || null,
  } as unknown as ReplyPromptConfig;
}

/**
 * Reply-style editor: system template chips + own wording on the left, a live
 * AI preview on the right. Autosaves; the preview renders the unsaved edit.
 */
export function ReplyStyleEditor({
  workspaceId,
  locationId,
  scope = 'location',
  dict,
}: {
  workspaceId: string;
  /** Required when `scope` is `location`. Also used for preview when `scope` is `workspace`. */
  locationId?: string;
  /** Workspace default vs per-location override editor. */
  scope?: 'workspace' | 'location';
  dict?: ReplyStyleEditorDict;
}) {
  const queryClient = useQueryClient();
  const { isFeatureEnabled } = useEntitlements();
  const aiEnabled = isFeatureEnabled('feature.reputation_ai');
  const { workspaces, refetch: refetchWorkspaces } = useWorkspaces();
  const setWorkspace = useSetWorkspace();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const { locations: previewLocations } = useLocations(workspaceId, {
    limit: 100,
    enabled: scope === 'workspace',
  });
  const [tone, setTone] = useState('');
  const [considerations, setConsiderations] = useState('');
  const [mustInclude, setMustInclude] = useState('');
  const [templateId, setTemplateId] = useState<string>(CUSTOM_TEMPLATE_VALUE);
  const [userPreviewLocationPicker, setUserPreviewLocationPicker] =
    useState('');
  const [pickIds, setPickIds] = useState<string[]>([]);
  const [pickIndex, setPickIndex] = useState(0);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const defaultPreviewLocationId = previewLocations[0]?.id ?? '';
  const previewLocationPicker =
    scope === 'workspace'
      ? userPreviewLocationPicker || defaultPreviewLocationId
      : '';

  const { data: location, isLoading: locationLoading } = useQuery({
    queryKey: locationsQueryKeys.detail(workspaceId, locationId),
    queryFn: async () => {
      if (!locationId) return null;
      const res = await LocationsApi.findById({
        path: { workspaceId, id: locationId },
      });
      return (res.data ?? null) as Location | null;
    },
    enabled: scope === 'location' && !!workspaceId && !!locationId,
  });

  type LocationWithReplyConfig = Location & {
    replyPromptConfig?: ReplyPromptConfig | null;
  };

  const locationConfig: ReplyPromptConfig | null =
    scope === 'location'
      ? ((location as LocationWithReplyConfig | null)?.replyPromptConfig ??
        null)
      : null;

  const workspaceConfig: ReplyPromptConfig | null =
    scope === 'workspace'
      ? ((workspace as Workspace | undefined)?.replyPromptConfig ?? null)
      : null;

  const config: ReplyPromptConfig | null =
    scope === 'workspace' ? workspaceConfig : locationConfig;

  const configLoading = scope === 'workspace' ? !workspace : locationLoading;

  const effectivePreviewLocationId =
    scope === 'location' ? locationId : previewLocationPicker;

  const { data: templates, isLoading: templatesLoading } =
    useReplyPromptTemplates(workspaceId);

  const { data: reviewsResponse } = useQuery({
    queryKey: reviewsQueryKeys.preview(workspaceId, effectivePreviewLocationId),
    queryFn: async () => {
      const res = await ReviewsApi.listReviews({
        path: { workspaceId, locationId: effectivePreviewLocationId as string },
        query: { provider: PROVIDER, limit: 50 },
      });
      return res.data as
        | {
            reviews: Array<{
              id: string;
              authorName: string;
              rating: number;
              text: string;
            }>;
          }
        | undefined;
    },
    enabled: !!workspaceId && !!effectivePreviewLocationId,
  });

  const reviews = reviewsResponse?.reviews ?? [];

  const lastSyncedLocationIdRef = useRef<string | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const [autosaveBaseline, setAutosaveBaseline] = useState<string | null>(null);

  useEffect(() => {
    const syncKey =
      scope === 'location' ? locationId : `workspace-${workspaceId}`;
    if (!syncKey || lastSyncedLocationIdRef.current === syncKey) return;
    if (scope === 'location' && !location) return;
    if (scope === 'workspace' && !workspace) return;
    const savedTemplateId = config?.templateId ?? '';
    const isPreset = isPresetTemplateId(savedTemplateId);
    lastSyncedLocationIdRef.current = syncKey;
    lastSavedRef.current = null;
    queueMicrotask(() => {
      setAutosaveBaseline(null);
      setTemplateId(isPreset ? savedTemplateId : CUSTOM_TEMPLATE_VALUE);
      // Preset copy lives on the backend — don't hydrate editable fields from
      // stored (possibly stale / key-path) tone fields.
      if (isPreset) {
        setTone('');
        setConsiderations('');
        setMustInclude('');
      } else {
        setTone(config?.tone ?? '');
        setConsiderations(config?.considerations ?? '');
        setMustInclude(config?.mustInclude ?? '');
      }
    });
  }, [
    scope,
    workspaceId,
    locationId,
    location,
    workspace,
    config?.tone,
    config?.considerations,
    config?.mustInclude,
    config?.templateId,
    config,
  ]);

  const [detachOpen, setDetachOpen] = useState(false);

  const isPresetSelected = isPresetTemplateId(templateId);
  const activePreset = useMemo(
    () =>
      isPresetSelected
        ? templates?.find((t) => t.id === templateId)
        : undefined,
    [isPresetSelected, templateId, templates],
  );
  const presetFields = useMemo(
    () => resolveReplyPromptTemplateFields(activePreset),
    [activePreset],
  );

  // Keep the three fields in sync with the live preset while a system template
  // is selected (templates may load after templateId is set).
  useEffect(() => {
    if (!isPresetSelected || !presetFields) return;
    queueMicrotask(() => {
      setTone(presetFields.tone);
      setConsiderations(presetFields.considerations);
      setMustInclude(presetFields.mustInclude);
    });
  }, [isPresetSelected, templateId, presetFields]);

  const saveMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async (payload: ReplyPromptConfig) => {
      if (scope === 'workspace') {
        await WorkspacesApi.update({
          path: { id: workspaceId },
          body: {
            replyPromptConfig: payload as unknown as ReplyPromptConfigDto,
          },
        });
        return;
      }
      if (!locationId) return;
      await LocationsApi.update({
        path: { workspaceId, id: locationId },
        body: {
          replyPromptConfig: payload as unknown as ReplyPromptConfigDto,
        } as unknown as UpdateLocationDto,
      });
    },
    onSuccess: async (_data, payload) => {
      const key = payloadSnapshot(payload);
      lastSavedRef.current = key;
      setAutosaveBaseline(key);
      if (scope === 'workspace') {
        const refreshed = await refetchWorkspaces();
        const updated = refreshed.find((w) => w.id === workspaceId);
        if (updated) setWorkspace(updated);
      } else {
        queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.detail(workspaceId, locationId),
        });
        queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      }
    },
    onError: () => {
      if (dict?.saveError) toast.error(dict.saveError);
    },
  });

  /** Build current payload from state (same shape as save). */
  const buildPayload = useCallback((): ReplyPromptConfig => {
    return buildReplyPromptPayload(
      templateId,
      tone,
      considerations,
      mustInclude,
      templates,
    );
  }, [tone, considerations, mustInclude, templateId, templates]);

  const baselineSavedKey = useMemo(() => {
    if (!templates) return null;
    return payloadSnapshot(
      buildReplyPromptPayload(
        isPresetTemplateId(config?.templateId ?? '')
          ? (config?.templateId ?? '')
          : CUSTOM_TEMPLATE_VALUE,
        config?.tone ?? '',
        config?.considerations ?? '',
        config?.mustInclude ?? '',
        templates,
      ),
    );
  }, [
    config?.tone,
    config?.considerations,
    config?.mustInclude,
    config?.templateId,
    templates,
  ]);

  // Establish hydrate baseline once templates + config are ready.
  useEffect(() => {
    if (baselineSavedKey == null) return;
    if (lastSavedRef.current != null) return;
    lastSavedRef.current = baselineSavedKey;
    queueMicrotask(() => {
      setAutosaveBaseline(baselineSavedKey);
    });
  }, [baselineSavedKey]);

  const liveSnapshot = useMemo(
    () => payloadSnapshot(buildPayload()),
    [buildPayload],
  );

  const persistConfig = useCallback(
    (payload: ReplyPromptConfig, immediate = false) => {
      const key = payloadSnapshot(payload);
      const savedKey = lastSavedRef.current ?? autosaveBaseline;
      if (savedKey !== null && key === savedKey) return;

      if (immediate) {
        saveMutation.mutate(payload);
      }
    },
    [autosaveBaseline, saveMutation],
  );

  useDebouncedAutosave({
    snapshot: liveSnapshot,
    baseline: autosaveBaseline,
    enabled: autosaveBaseline != null && !configLoading,
    delayMs: 800,
    onSave: () => {
      saveMutation.mutate(buildPayload());
    },
  });

  const detachToCustom = useCallback(() => {
    const nextTone = presetFields?.tone ?? '';
    const nextConsiderations = presetFields?.considerations ?? '';
    const nextMustInclude = presetFields?.mustInclude ?? '';
    setTemplateId(CUSTOM_TEMPLATE_VALUE);
    setTone(nextTone);
    setConsiderations(nextConsiderations);
    setMustInclude(nextMustInclude);
    setDetachOpen(false);
    persistConfig(
      buildReplyPromptPayload(
        CUSTOM_TEMPLATE_VALUE,
        nextTone,
        nextConsiderations,
        nextMustInclude,
        templates,
      ),
      true,
    );
  }, [presetFields, templates, persistConfig, setDetachOpen]);

  const requestDetach = () => {
    if (isPresetSelected) setDetachOpen(true);
  };

  const handleToneChange = (value: string) => {
    if (isPresetSelected) {
      requestDetach();
      return;
    }
    setTone(value);
  };
  const handleConsiderationsChange = (value: string) => {
    if (isPresetSelected) {
      requestDetach();
      return;
    }
    setConsiderations(value);
  };
  const handleMustIncludeChange = (value: string) => {
    if (isPresetSelected) {
      requestDetach();
      return;
    }
    setMustInclude(value);
  };

  // Live preview: one sample at a time, rendered from the *unsaved* edit.
  const reviewsRef = useRef(reviews);
  useEffect(() => {
    reviewsRef.current = reviews;
  });
  const reviewsReady = reviews.length > 0;

  const previewMutation = useMutation({
    mutationFn: async ({
      reviewId,
      payload,
    }: {
      reviewId: string;
      payload: ReplyPromptConfig;
    }) => {
      if (!effectivePreviewLocationId) {
        throw new Error('missingPreviewLocation');
      }
      const res = await ReviewsApi.suggestReplyPreview({
        path: { workspaceId, locationId: effectivePreviewLocationId },
        body: {
          reviewIds: [reviewId],
          replyPromptConfig: payload as unknown as ReplyPromptConfigDto,
        },
      });
      return res.data?.suggestions?.[0]?.suggestedText ?? '';
    },
    onSuccess: (text) => setPreviewText(text),
    onError: () => {
      toast.error(
        dict?.previewError ?? 'Preview konnte nicht generiert werden.',
      );
    },
  });

  // Fresh sample set whenever the preview location (or its reviews) change.
  const pickSeedKey = `${effectivePreviewLocationId}:${reviewsReady}`;
  const lastPickSeedRef = useRef('');
  useEffect(() => {
    if (!reviewsReady || lastPickSeedRef.current === pickSeedKey) return;
    lastPickSeedRef.current = pickSeedKey;
    queueMicrotask(() => {
      setPickIds(pickRandomReviewIds(reviewsRef.current, PREVIEW_SAMPLE_COUNT));
      setPickIndex(0);
      setPreviewText(null);
    });
  }, [pickSeedKey, reviewsReady]);

  const currentReviewId = pickIds[pickIndex];
  const previewEnabled =
    aiEnabled &&
    !!currentReviewId &&
    autosaveBaseline != null &&
    !configLoading &&
    !templatesLoading;

  const { mutate: runPreview } = previewMutation;
  // Debounced: re-render the sample when the style (template / wording) or the
  // sample review changes. Never saves — the endpoint takes the config inline.
  useEffect(() => {
    if (!previewEnabled || !currentReviewId) return;
    const timer = window.setTimeout(() => {
      runPreview({ reviewId: currentReviewId, payload: buildPayload() });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [previewEnabled, currentReviewId, liveSnapshot, buildPayload, runPreview]);

  const nextSample = () => {
    if (pickIds.length === 0) return;
    setPreviewText(null);
    setPickIndex((i) => (i + 1) % pickIds.length);
  };

  const handleTemplateChange = (value: string) => {
    if (isPresetTemplateId(value)) {
      const next = resolveReplyPromptTemplateFields(
        templates?.find((t) => t.id === value),
      );
      setTemplateId(value);
      setTone(next?.tone ?? '');
      setConsiderations(next?.considerations ?? '');
      setMustInclude(next?.mustInclude ?? '');
      persistConfig(
        buildReplyPromptPayload(value, '', '', '', templates),
        true,
      );
      return;
    }

    // Switching to own template from a preset: seed with live preset copy.
    if (isPresetSelected && presetFields) {
      setTemplateId(CUSTOM_TEMPLATE_VALUE);
      setTone(presetFields.tone);
      setConsiderations(presetFields.considerations);
      setMustInclude(presetFields.mustInclude);
      persistConfig(
        buildReplyPromptPayload(
          CUSTOM_TEMPLATE_VALUE,
          presetFields.tone,
          presetFields.considerations,
          presetFields.mustInclude,
          templates,
        ),
        true,
      );
      return;
    }

    setTemplateId(CUSTOM_TEMPLATE_VALUE);
    persistConfig(
      buildReplyPromptPayload(
        CUSTOM_TEMPLATE_VALUE,
        tone,
        considerations,
        mustInclude,
        templates,
      ),
      true,
    );
  };

  if (configLoading) {
    return (
      <div className="flex min-w-0 w-full flex-col gap-4">
        <Skeleton className="h-64 w-full rounded-[var(--r-card)]" />
      </div>
    );
  }

  const templateLabel = dict?.templateLabel ?? 'Vorlage';
  const presetHint =
    dict?.presetLockedHint ??
    'Vorlage vom System verwaltet. Tippe in ein Feld, um zu einer eigenen Vorlage zu wechseln.';

  const fields = [
    {
      key: 'tone',
      label: dict?.toneLabel ?? 'Tonalität',
      value: tone,
      onChange: handleToneChange,
      max: REPLY_PROMPT_CONFIG_MAX_LENGTHS.tone,
      placeholder: 'z. B. freundlich und kurz',
    },
    {
      key: 'considerations',
      label: dict?.considerationsLabel ?? 'Was zu berücksichtigen ist',
      value: considerations,
      onChange: handleConsiderationsChange,
      max: REPLY_PROMPT_CONFIG_MAX_LENGTHS.considerations,
      placeholder: 'Optional',
    },
    {
      key: 'mustInclude',
      label: dict?.mustIncludeLabel ?? 'Was die Antwort enthalten soll',
      value: mustInclude,
      onChange: handleMustIncludeChange,
      max: REPLY_PROMPT_CONFIG_MAX_LENGTHS.mustInclude,
      placeholder: 'Optional',
    },
  ];

  const previewReview = reviews.find((r) => r.id === currentReviewId);
  const previewLocationName =
    previewLocations.find((l) => l.id === effectivePreviewLocationId)?.name ??
    '';

  return (
    <div className="flex min-w-0 w-full flex-col gap-3">
      <div
        className={cn(
          'grid min-w-0 items-stretch gap-3.5',
          aiEnabled && 'xl:grid-cols-2',
        )}
      >
        <Card className="flex min-w-0 flex-col gap-4 p-4 sm:p-[18px]">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-0.5">
              <Label>{templateLabel}</Label>
              {isPresetSelected ? (
                <SettingsInfoHint label={templateLabel}>
                  {presetHint}
                </SettingsInfoHint>
              ) : null}
            </div>
            {templatesLoading ? (
              <Skeleton className="h-8 w-full max-w-sm rounded-full" />
            ) : (
              <div className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] md:flex-wrap md:overflow-visible [&::-webkit-scrollbar]:hidden [&>*]:shrink-0">
                {templates?.map((t) => {
                  const selected = isPresetSelected && templateId === t.id;
                  return (
                    <Button
                      key={t.id ?? ''}
                      type="button"
                      size="sm"
                      variant={selected ? 'primary' : 'outline'}
                      aria-pressed={selected}
                      className="h-8 rounded-full px-3 text-[12.5px]"
                      onClick={() => handleTemplateChange(t.id ?? '')}
                    >
                      {dict?.templates?.[templateIdToKey(t.id ?? '')]?.name ??
                        t.name}
                    </Button>
                  );
                })}
                <Button
                  type="button"
                  size="sm"
                  variant={!isPresetSelected ? 'primary' : 'dashed'}
                  aria-pressed={!isPresetSelected}
                  className="h-8 rounded-full px-3 text-[12.5px]"
                  onClick={() => handleTemplateChange(CUSTOM_TEMPLATE_VALUE)}
                >
                  {dict?.customLabel ?? 'Eigene'}
                </Button>
              </div>
            )}
          </div>

          {fields.map((field) => (
            <div
              key={field.key}
              // Mobile design shows only chips + preview for a system template.
              className={cn(
                'flex flex-col gap-1.5',
                isPresetSelected && 'max-md:hidden',
              )}
            >
              <Label>{field.label}</Label>
              <Textarea
                key={`${field.key}-${templateId}`}
                value={field.value}
                readOnly={isPresetSelected}
                onChange={(e) => field.onChange(e.target.value)}
                onFocus={(e) => {
                  if (!isPresetSelected) return;
                  e.currentTarget.blur();
                  requestDetach();
                }}
                placeholder={field.placeholder}
                aria-label={field.label}
                rows={2}
                className="min-w-0"
                maxLength={field.max}
              />
              {!isPresetSelected ? (
                <p className="text-[12px] leading-snug text-muted-foreground/85">
                  {field.value.length} / {field.max}
                </p>
              ) : null}
            </div>
          ))}
        </Card>

        {aiEnabled ? (
          <Card
            className={cn(
              'flex min-w-0 flex-col gap-3 p-4 shadow-none sm:p-[18px]',
              previewPanelSurfaceClass,
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <span className={REPUTATION_SECTION_LABEL_CLASS}>
                {dict?.previewTitle ?? 'Live-Vorschau'}
              </span>
              {scope === 'workspace' && previewLocations.length > 1 ? (
                <Select
                  value={previewLocationPicker}
                  onValueChange={setUserPreviewLocationPicker}
                >
                  <SelectTrigger className="h-8 w-auto min-w-36 gap-2 text-xs">
                    <SelectValue
                      placeholder={
                        dict?.previewLocationPlaceholder ?? 'Standort…'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {previewLocations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>

            {!previewReview ? (
              <p className="text-muted-foreground text-[13px]">
                {dict?.noReviewsForPreview ??
                  'Noch keine Bewertungen für diesen Standort.'}
              </p>
            ) : (
              <>
                <div className="rounded-[var(--radius-nested)] border bg-card p-3.5">
                  <div className="flex items-center gap-2">
                    <ReviewAuthorAvatar
                      name={previewReview.authorName}
                      className="size-[26px] text-[10px]"
                    />
                    <span className="text-[13px] font-semibold">
                      {previewReview.authorName}
                    </span>
                    <span className="ml-auto">
                      <StarRating rating={previewReview.rating} />
                    </span>
                  </div>
                  {previewReview.text ? (
                    <p className="text-muted-foreground mt-2 text-[13px] leading-normal">
                      „{previewReview.text}“
                    </p>
                  ) : null}
                </div>
                <div className="ml-6 rounded-[var(--radius-nested)] border border-[color:var(--ghost-accent-border)] bg-card p-3.5">
                  <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-[color:var(--ghost-accent-fg)]">
                    <Sparkles className="size-3.5" aria-hidden />
                    {(dict?.replyFrom ?? 'Antwort von {name}').replace(
                      '{name}',
                      previewLocationName,
                    )}
                  </div>
                  {previewText == null || previewMutation.isPending ? (
                    <div className="mt-2 flex flex-col gap-2">
                      <Skeleton className="h-3.5 w-full" />
                      <Skeleton className="h-3.5 w-4/5" />
                    </div>
                  ) : (
                    <p className="mt-1.5 text-[13px] leading-[1.55]">
                      {previewText}
                    </p>
                  )}
                </div>
                <div className="mt-auto flex items-center gap-2.5 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={pickIds.length < 2 || previewMutation.isPending}
                    onClick={nextSample}
                  >
                    <RefreshCw className="mr-1.5 size-3.5" aria-hidden />
                    {dict?.otherReview ?? 'Andere Bewertung'}
                  </Button>
                  <span className="text-muted-foreground text-[11.5px]">
                    {(dict?.exampleOf ?? '{n} von {total} Beispielen')
                      .replace('{n}', String(pickIndex + 1))
                      .replace('{total}', String(pickIds.length))}
                  </span>
                </div>
              </>
            )}
          </Card>
        ) : null}
      </div>

      <AlertDialog open={detachOpen} onOpenChange={setDetachOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dict?.detachTitle ?? 'Eigene Vorlage erstellen?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dict?.detachDescription ??
                'Die aktuelle Vorlage bleibt vom System verwaltet. Wenn du bearbeitest, wird sie als eigene Vorlage gespeichert — spätere Updates der System-Vorlage greifen dann nicht mehr.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {dict?.detachCancel ?? 'Abbrechen'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={detachToCustom}>
              {dict?.detachConfirm ?? 'Bearbeiten'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
